import { Navigate, useNavigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import { CERTIFICATE_LABELS } from '../../constants/labels';
import { CertificatePreview } from './CertificatePreview';
import { useViewCertificateTemplate } from './useViewCertificateTemplate';

const formatCreatedDate = (iso: string | null | undefined): string => {
  if (!iso) {
    return CERTIFICATE_LABELS.VIEW_VALUE_EMPTY;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return CERTIFICATE_LABELS.VIEW_VALUE_EMPTY;
  }
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')}`;
};

interface DetailItemProps {
  label: string;
  value: string;
}

const DetailItem = ({ label, value }: DetailItemProps) => (
  <div className="flex flex-col gap-1 min-w-0">
    <p className="text-[12px] leading-4 tracking-[0.4px] text-[#474A4C]">
      {label}
    </p>
    <p className="text-[16px] leading-6 tracking-[0.5px] text-[#181A1B] break-words">
      {value}
    </p>
  </div>
);

interface ViewCertificateTemplateProps {
  templateId: string;
}

export default function ViewCertificateTemplate({
  templateId,
}: ViewCertificateTemplateProps) {
  const navigate = useNavigate();
  const { loading, template, notFound, error } =
    useViewCertificateTemplate(templateId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (error || !template) {
    return (
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4">
        <p className="text-sm text-[#E11B1B]">
          {error || CERTIFICATE_LABELS.VIEW_ERROR_GENERIC}
        </p>
      </div>
    );
  }

  const taskFormName =
    template.formTemplate?.name ?? CERTIFICATE_LABELS.VIEW_VALUE_EMPTY;
  const taskFormId =
    template.formTemplate?.formTemplateId ??
    CERTIFICATE_LABELS.VIEW_VALUE_EMPTY;
  const createdDate = formatCreatedDate(template.createdAt);

  return (
    <div
      data-testid="view-certificate-template-container"
      className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden"
    >
      {/* Page title */}
      <div className="shrink-0 py-2.5">
        <h1 className="text-[22px] font-normal leading-7 text-[#181A1B]">
          {CERTIFICATE_LABELS.VIEW_PAGE_TITLE}
        </h1>
      </div>

      {/* Body — whole region scrolls together (name, details and certificate),
          nothing pinned, matching the create/edit preview screens. */}
      <div className="flex-1 min-h-0 flex flex-col gap-2 overflow-auto custom-scrollbar">
        {/* Name + Edit button */}
        <div className="shrink-0 flex items-center justify-between gap-4 py-1">
          <p className="text-[18px] font-normal leading-[26px] text-[#474A4C] min-w-0 break-words">
            {template.certTemplateName}
          </p>
          <button
            type="button"
            data-testid="view-certificate-template-edit-button"
            onClick={() =>
              navigate(`/Certyfikaty/Edycja-szablonu/${templateId}`, {
                state: {
                  returnTo: `/Certyfikaty/Podgląd-szablonu/${templateId}`,
                },
              })
            }
            className="shrink-0 h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer"
          >
            <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
              {CERTIFICATE_LABELS.VIEW_BUTTON_EDIT}
            </span>
          </button>
        </div>

        {/* Details card */}
        <div className="rounded-lg border border-[#DCDDE0] p-4 flex flex-col gap-4">
          <p className="text-[18px] leading-[26px] text-[#474A4C]">
            {CERTIFICATE_LABELS.VIEW_DETAILS_TITLE}
          </p>
          <div className="grid grid-cols-3 gap-x-16 gap-y-8">
            <DetailItem
              label={CERTIFICATE_LABELS.VIEW_FIELD_TEMPLATE_ID}
              value={template.certTemplateId}
            />
            <DetailItem
              label={CERTIFICATE_LABELS.VIEW_FIELD_TASK_FORM}
              value={taskFormName}
            />
            <DetailItem
              label={CERTIFICATE_LABELS.VIEW_FIELD_TASK_FORM_ID}
              value={taskFormId}
            />
            <DetailItem
              label={CERTIFICATE_LABELS.VIEW_FIELD_CREATED_DATE}
              value={createdDate}
            />
          </div>
        </div>

        {/* Certificate preview — let the body scroll handle it (no inner scroll) */}
        <CertificatePreview template={template} className="w-full bg-white" />
      </div>
    </div>
  );
}
